import React, { useCallback, useEffect, useState, useRef } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useCssHandles } from '../../../shared/hooks/useCssHandles'
import { AdminLayout } from '../components'
import { Modal, ConfirmModal, ImageWithFallback, Select, Input, Textarea, FileInput } from '../../../shared/components'
import { AdminFilterTrigger, AdminFilterSidebar } from '../components'
import adminBrandService, { Brand, BrandFormValues } from '../../../services/adminBrandService'
import { buildImageUrl } from '../../../config'

const CSS_HANDLES = [
  'adminBrands',
  'brandsHeader',
  'brandsTitle',
  'brandsSubtitle',
  'brandsActions',
  'addBrandButton',
  'brandsTable',
  'tableHeader',
  'tableRow',
  'brandLogo',
  'brandInfo',
  'brandName',
  'brandStatus',
  'brandActions',
  'editButton',
  'deleteButton',
  'statusToggle',
  'statusActive',
  'statusInactive',
  'modal',
  'modalOverlay',
  'modalContent',
  'modalHeader',
  'modalTitle',
  'modalClose',
  'modalBody',
  'formGroup',
  'formLabel',
  'formInput',
  'formTextarea',
  'formSelect',
  'formRow',
  'modalFooter',
  'cancelButton',
  'saveButton',
  'pagination',
  'paginationButton',
  'paginationInfo'
] as const

const AdminBrands: React.FC = () => {
  const handles = useCssHandles(CSS_HANDLES)
  const [brands, setBrands] = useState<Brand[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pagination, setPagination] = useState<{ page: number; limit: number; total: number; totalPages: number } | null>(null)
  const [isFetching, setIsFetching] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false)
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [pendingDeletion, setPendingDeletion] = useState<string | null>(null)
  const [pendingStatusToggle, setPendingStatusToggle] = useState<string | null>(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [brandToDelete, setBrandToDelete] = useState<Brand | null>(null)

  type BrandFormData = Omit<BrandFormValues, 'isActive'> & {
    isActive: string
  }

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors }
  } = useForm<BrandFormData>({
    defaultValues: {
      name: '',
      description: '',
      website: '',
      isActive: 'true',
      sortOrder: 0
    }
  })

  const defaultFilters = { search: '', isActive: undefined as boolean | undefined }

  const [filters, setFilters] = useState<{ search: string; isActive?: boolean }>(() => ({
    ...defaultFilters
  }))
  const [draftFilters, setDraftFilters] = useState<{ search: string; isActive?: boolean }>(() => ({
    ...defaultFilters
  }))

  const itemsPerPage = 20

  const resolveErrorMessage = (err: any, defaultMessage: string): string => {
    if (err?.message) return err.message
    if (err?.response?.data?.message) return err.response.data.message
    if (typeof err?.response?.data === 'string') return err.response.data
    return defaultMessage
  }

  const previousFiltersRef = useRef<string | null>(null)
  const previousPageRef = useRef<number>(1)
  const isLoadingRef = useRef(false)

  const fetchBrands = useCallback(async (page: number, force: boolean = false) => {
    // Serializar filters para comparação
    const filtersKey = JSON.stringify({ search: filters.search.trim(), isActive: filters.isActive })
    
    // Verificar se filters ou page realmente mudaram
    const filtersChanged = previousFiltersRef.current !== filtersKey
    const pageChanged = previousPageRef.current !== page

    // Se não mudou e já está carregando, não fazer nada (a menos que force seja true)
    if (!force && !filtersChanged && !pageChanged && isLoadingRef.current) {
      return
    }

    // Se não mudou nada e já foi carregado, não fazer nada (evita chamadas duplicadas no React Strict Mode)
    // A menos que force seja true
    if (!force && !filtersChanged && !pageChanged && !isLoadingRef.current && previousFiltersRef.current === filtersKey) {
      return
    }

    isLoadingRef.current = true
    previousFiltersRef.current = filtersKey
    previousPageRef.current = page

    setIsFetching(true)
    setError(null)

    try {
      const response = await adminBrandService.list({
        page,
        limit: itemsPerPage,
        search: filters.search.trim() || undefined,
        isActive: filters.isActive
      })

      setBrands(response.brands)
      setPagination(response.pagination)
      setCurrentPage(response.pagination.page)
      setHasLoaded(true)
    } catch (err) {
      console.error('Erro ao buscar marcas:', err)
      setError(resolveErrorMessage(err, 'Não foi possível carregar as marcas.'))
      setHasLoaded(true)
    } finally {
      setIsFetching(false)
      isLoadingRef.current = false
    }
  }, [filters, itemsPerPage])

  useEffect(() => {
    fetchBrands(1)
  }, [fetchBrands])

  const hasActiveFilters = filters.search.trim() !== '' || filters.isActive !== undefined

  const handleOpenFilters = () => {
    setDraftFilters({ ...filters })
    setIsFilterSidebarOpen(true)
  }

  const handleCloseFilters = () => {
    setIsFilterSidebarOpen(false)
  }

  const handleDraftFilterChange = <Key extends keyof typeof draftFilters>(key: Key, value: (typeof draftFilters)[Key]) => {
    setDraftFilters((prev) => ({
      ...prev,
      [key]: value
    }))
  }

  const handleApplyFilters = () => {
    setFilters({ ...draftFilters })
    setCurrentPage(1)
    setIsFilterSidebarOpen(false)
  }

  const handleClearFilters = () => {
    const reset = { ...defaultFilters }
    setDraftFilters(reset)
    setFilters(reset)
    setCurrentPage(1)
  }

  const handleAddBrand = () => {
    setActionError(null)
    setEditingBrand(null)
    setLogoFile(null)
    setLogoPreview(null)
    setIsModalOpen(true)
  }

  const handleEditBrand = (brand: Brand) => {
    setActionError(null)
    setEditingBrand(brand)
    setLogoFile(null)
    const logoUrl = buildImageUrl(`/uploads/brands/${brand.slug}.webp?t=${brand.updatedAt}`) as string
    setLogoPreview(logoUrl)
    setIsModalOpen(true)
  }

  const handleDeleteBrand = (brand: Brand) => {
    setBrandToDelete(brand)
    setDeleteConfirmOpen(true)
  }

  const confirmDeleteBrand = async () => {
    if (!brandToDelete) return

    setPendingDeletion(brandToDelete.id)
    setActionError(null)
    setDeleteConfirmOpen(false)

    try {
      await adminBrandService.remove(brandToDelete.id)

      const isLastItemOnPage = brands.length === 1 && currentPage > 1
      const nextPage = isLastItemOnPage ? currentPage - 1 : currentPage

      if (isLastItemOnPage) {
        setCurrentPage(nextPage)
      }

      await fetchBrands(nextPage, true)
    } catch (err) {
      console.error('Erro ao excluir marca:', err)
      setActionError(
        resolveErrorMessage(err, 'Não foi possível excluir a marca. Tente novamente.')
      )
    } finally {
      setPendingDeletion(null)
      setBrandToDelete(null)
    }
  }

  const handleToggleStatus = async (brandId: string, currentStatus: boolean) => {
    setPendingStatusToggle(brandId)
    setActionError(null)

    try {
      await adminBrandService.updateStatus(brandId, !currentStatus)
      await fetchBrands(currentPage, true)
    } catch (err) {
      console.error('Erro ao atualizar status da marca:', err)
      setActionError(
        resolveErrorMessage(err, 'Não foi possível atualizar o status da marca.')
      )
    } finally {
      setPendingStatusToggle(null)
    }
  }

  const onSubmitBrand = async (data: BrandFormData) => {
    setIsSaving(true)
    setActionError(null)

    const formValues: BrandFormValues = {
      name: data.name,
      description: data.description || undefined,
      website: data.website || undefined,
      isActive: data.isActive === 'true',
      sortOrder: data.sortOrder || 0
    }

    try {
      if (editingBrand) {
        await adminBrandService.update(editingBrand.id, formValues, logoFile || undefined)
        await fetchBrands(currentPage, true)
      } else {
        await adminBrandService.create(formValues, logoFile || undefined)
        setCurrentPage(1)
        await fetchBrands(1, true)
      }

      setIsModalOpen(false)
      setEditingBrand(null)
      setLogoFile(null)
      setLogoPreview(null)
      reset({
        name: '',
        description: '',
        website: '',
        isActive: 'true',
        sortOrder: 0
      })
    } catch (err) {
      console.error('Erro ao salvar marca:', err)
      const message = resolveErrorMessage(
        err,
        'Não foi possível salvar a marca. Verifique os dados e tente novamente.'
      )
      setActionError(message)
      throw err
    } finally {
      setIsSaving(false)
    }
  }

  // Reset form when editing brand changes
  useEffect(() => {
    if (editingBrand) {
      reset({
        name: editingBrand.name || '',
        description: editingBrand.description || '',
        website: editingBrand.website || '',
        isActive: editingBrand.isActive ? 'true' : 'false',
        sortOrder: editingBrand.sortOrder || 0
      })
      const logoUrl = buildImageUrl(`/uploads/brands/${editingBrand.slug}.webp?t=${editingBrand.updatedAt}`) as string
      setLogoPreview(logoUrl)
      setLogoFile(null)
    } else {
      reset({
        name: '',
        description: '',
        website: '',
        isActive: 'true',
        sortOrder: 0
      })
      setLogoPreview(null)
      setLogoFile(null)
    }
  }, [editingBrand, reset])

  const isApplyDisabled =
    draftFilters.search === filters.search && draftFilters.isActive === filters.isActive

  const totalPages = pagination?.totalPages ?? 1
  const totalItems = pagination?.total ?? brands.length
  const currentPageNumber = pagination?.page ?? currentPage
  const limit = pagination?.limit ?? itemsPerPage
  const startIndex = totalItems === 0 ? 0 : (currentPageNumber - 1) * limit
  const endIndex = totalItems === 0 ? 0 : Math.min(startIndex + brands.length, totalItems)
  const showLoadingOverlay = hasLoaded && isFetching
  const activeFiltersDescriptions = [
    filters.isActive !== undefined ? `status: ${filters.isActive ? 'ativos' : 'inativos'}` : null,
    filters.search.trim() ? `busca: "${filters.search.trim()}"` : null
  ]
    .filter(Boolean)
    .join(' • ')

  const renderSkeleton = () => (
    <AdminLayout>
      <div className="space-y-8">
        <div className="space-y-3">
          <div className="h-8 w-64 animate-pulse rounded bg-gray-200" />
          <div className="h-4 w-80 animate-pulse rounded bg-gray-200" />
        </div>

        <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="h-10 w-full animate-pulse rounded bg-gray-200 md:w-80" />
            <div className="h-10 w-full animate-pulse rounded bg-gray-200 md:w-48" />
          </div>
        </div>

        <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="mb-4 h-6 w-48 animate-pulse rounded bg-gray-200" />
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={`skeleton-row-${index}`} className="h-16 animate-pulse rounded bg-gray-100" />
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  )

  if (!hasLoaded && isFetching) {
    return renderSkeleton()
  }

  if (error && !hasLoaded && !isFetching) {
    return (
      <AdminLayout>
        <div className="flex min-h-[50vh] items-center justify-center">
          <div className="space-y-4 text-center">
            <p className="text-gray-600">{error}</p>
            <button
              onClick={() => fetchBrands(1)}
              className="rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <div className="relative">
        {showLoadingOverlay && (
          <div className="absolute inset-0 z-40 flex items-center justify-center bg-white/60">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
          </div>
        )}

        <div className={`${handles.adminBrands} min-h-screen bg-gray-50`}>
          <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
            <div className={`${handles.brandsHeader}`}>
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <h1 className={`${handles.brandsTitle} text-3xl font-bold text-gray-900 mb-2`}>
                    Gestão de Marcas
                  </h1>
                  <p className={`${handles.brandsSubtitle} text-lg text-gray-600`}>
                    Gerencie as marcas do catálogo
                  </p>
                </div>
                <button
                  onClick={handleAddBrand}
                  className={`${handles.addBrandButton} inline-flex items-center justify-center rounded-lg bg-blue-600 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2`}
                >
                  Adicionar Marca
                </button>
              </div>
            </div>

            {actionError && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                {actionError}
              </div>
            )}

            {error && hasLoaded && !isFetching && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <span>{error}</span>
                  <button
                    onClick={() => fetchBrands(currentPageNumber)}
                    className="inline-flex items-center text-red-700 underline hover:text-red-800"
                  >
                    Tentar novamente
                  </button>
                </div>
              </div>
            )}

            <div className={`${handles.brandsActions} rounded-xl bg-white p-6 shadow-sm`}>
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-sm text-gray-600">
                    Exibindo{' '}
                    <span className="font-semibold text-gray-900">
                      {totalItems === 0 ? 0 : startIndex + 1} - {endIndex}
                    </span>{' '}
                    de{' '}
                    <span className="font-semibold text-gray-900">{totalItems}</span>
                    {' '}marcas
                  </p>
                  {hasActiveFilters && (
                    <p className="mt-1 text-xs text-blue-600">
                      Filtros aplicados: {activeFiltersDescriptions}
                    </p>
                  )}
                </div>
                <AdminFilterTrigger
                  onClick={handleOpenFilters}
                  hasActiveFilters={hasActiveFilters}
                />
              </div>
            </div>

            <div className="rounded-lg bg-white shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className={`${handles.brandsTable} w-full`}>
                  <thead className={`${handles.tableHeader} bg-gray-50`}>
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Logo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Nome
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Status
                      </th>
                      <th className="sticky right-0 z-20 bg-gray-50 px-2 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500 shadow-[inset_1px_0_0_0_rgb(226,232,240)] w-24">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {brands.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-6 py-10 text-center text-sm text-gray-500">
                          Nenhuma marca encontrada para os filtros selecionados.
                        </td>
                      </tr>
                    ) : (
                      brands.map((brand) => (
                        <tr key={brand.id} className={`${handles.tableRow} hover:bg-gray-50`}>
                          <td className="whitespace-nowrap px-6 py-4">
                            <div className={`${handles.brandLogo} flex h-12 w-12 items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-gray-50`}>
                              <ImageWithFallback
                                key={`${brand.id}-${brand.updatedAt}`}
                                src={buildImageUrl(`/uploads/brands/${brand.slug}.webp?t=${brand.updatedAt}`)}
                                alt={brand.name}
                                className="h-full w-full object-cover"
                                fallbackWrapperClassName="flex h-full w-full items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100"
                                fallbackIconClassName="h-6 w-6 text-gray-300"
                              />
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4">
                            <div className={`${handles.brandInfo}`}>
                              <div className={`${handles.brandName} text-sm font-medium text-gray-900`}>
                                {brand.name}
                              </div>
                              {brand.description && (
                                <div className="text-xs text-gray-500 mt-1">
                                  {brand.description.substring(0, 50)}...
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4">
                            <button
                              onClick={() => handleToggleStatus(brand.id, brand.isActive)}
                              disabled={pendingStatusToggle === brand.id}
                              className={`${handles.statusToggle} ${
                                brand.isActive
                                  ? `${handles.statusActive} bg-green-100 text-green-800`
                                  : `${handles.statusInactive} bg-red-100 text-red-800`
                              } rounded-full px-3 py-1 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                                brand.isActive ? 'focus-visible:ring-green-300' : 'focus-visible:ring-red-300'
                              } disabled:cursor-not-allowed disabled:opacity-60`}
                            >
                              {pendingStatusToggle === brand.id
                                ? 'Atualizando...'
                                : brand.isActive
                                ? 'Ativo'
                                : 'Inativo'}
                            </button>
                          </td>
                          <td className="sticky right-0 z-10 whitespace-nowrap bg-white px-2 py-4 text-sm font-medium shadow-[inset_1px_0_0_0_rgb(226,232,240)] w-24">
                            <div className={`${handles.brandActions} flex space-x-2 justify-center`}>
                              <button
                                onClick={() => handleEditBrand(brand)}
                                title="Editar marca"
                                aria-label="Editar marca"
                                className={`${handles.editButton} inline-flex h-9 w-9 items-center justify-center rounded-md bg-yellow-400 text-gray-900 shadow-sm transition-colors hover:bg-yellow-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-300 focus-visible:ring-offset-2`}
                              >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M16.862 3.487a2.1 2.1 0 112.971 2.97L7.5 18.79 3 21l2.21-4.5 11.652-12.013z" />
                                </svg>
                                <span className="sr-only">Editar marca</span>
                              </button>
                              <button
                                onClick={() => handleDeleteBrand(brand)}
                                title="Excluir marca"
                                aria-label="Excluir marca"
                                disabled={pendingDeletion === brand.id}
                                aria-busy={pendingDeletion === brand.id}
                                className={`${handles.deleteButton} inline-flex h-9 w-9 items-center justify-center rounded-md bg-yellow-400 text-gray-900 shadow-sm transition-colors hover:bg-yellow-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-300 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60`}
                              >
                                {pendingDeletion === brand.id ? (
                                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-700 border-t-transparent" aria-hidden="true" />
                                ) : (
                                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3m-4 0h14" />
                                  </svg>
                                )}
                                <span className="sr-only">
                                  {pendingDeletion === brand.id ? 'Excluindo marca' : 'Excluir marca'}
                                </span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className={`${handles.pagination} bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200`}>
                  <div className={`${handles.paginationInfo} text-sm text-gray-700`}>
                    {totalItems > 0
                      ? `Mostrando ${startIndex + 1} a ${endIndex} de ${totalItems} marcas`
                      : 'Nenhuma marca encontrada'}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                      disabled={currentPageNumber === 1}
                      className={`${handles.paginationButton} px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50`}
                    >
                      Anterior
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`${handles.paginationButton} px-3 py-1 border rounded-md ${
                          page === currentPageNumber
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                      disabled={currentPageNumber === totalPages}
                      className={`${handles.paginationButton} px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50`}
                    >
                      Próximo
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <AdminFilterSidebar
        isOpen={isFilterSidebarOpen}
        onClose={handleCloseFilters}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        isApplyDisabled={isApplyDisabled}
        applyLabel="Aplicar filtros"
        clearLabel="Limpar filtros"
      >
        <div className="space-y-6">
          <div>
            <label
              htmlFor="brand-filter-search"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Buscar marcas
            </label>
            <Input
              id="brand-filter-search"
              type="text"
              value={draftFilters.search}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleDraftFilterChange('search', event.target.value)
              }
              placeholder="Nome da marca"
            />
          </div>

          <div>
            <label
              htmlFor="brand-filter-status"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Status
            </label>
            <Select
              id="brand-filter-status"
              name="brand-filter-status"
              value={draftFilters.isActive === undefined ? 'all' : draftFilters.isActive ? 'active' : 'inactive'}
              onChange={(value) =>
                handleDraftFilterChange(
                  'isActive',
                  value === 'all' ? undefined : value === 'active'
                )
              }
              options={[
                { value: 'all', label: 'Todos os status' },
                { value: 'active', label: 'Ativos' },
                { value: 'inactive', label: 'Inativos' }
              ]}
              placeholder="Todos os status"
              className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </AdminFilterSidebar>

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setEditingBrand(null)
          setLogoFile(null)
          setLogoPreview(null)
          reset({
            name: '',
            description: '',
            website: '',
            isActive: 'true',
            sortOrder: 0
          })
        }}
        title={editingBrand ? 'Editar Marca' : 'Nova Marca'}
      >
        <form onSubmit={handleSubmit(onSubmitBrand)} className="space-y-4">
          {actionError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {actionError}
            </div>
          )}

          <div className={`${handles.formGroup}`}>
            <label htmlFor="name" className={`${handles.formLabel} block text-sm font-medium text-gray-700 mb-1.5`}>
              Nome *
            </label>
            <Input
              type="text"
              id="name"
              {...register('name', {
                required: 'Nome é obrigatório'
              })}
              error={!!errors.name}
              helperText={errors.name?.message}
            />
          </div>

          <div className={`${handles.formGroup}`}>
            <label htmlFor="description" className={`${handles.formLabel} block text-sm font-medium text-gray-700 mb-1.5`}>
              Descrição
            </label>
            <Textarea
              id="description"
              {...register('description')}
              rows={3}
            />
          </div>

          <div className={`${handles.formGroup}`}>
            <label htmlFor="website" className={`${handles.formLabel} block text-sm font-medium text-gray-700 mb-1.5`}>
              Website
            </label>
            <Input
              type="url"
              id="website"
              {...register('website', {
                pattern: {
                  value: /^https?:\/\/.+/,
                  message: 'URL inválida. Deve começar com http:// ou https://'
                }
              })}
              error={!!errors.website}
              helperText={errors.website?.message}
            />
          </div>

          <div className={`${handles.formGroup}`}>
            <FileInput
              id="logo"
              accept="image/*"
              onChange={(file) => {
                setLogoFile(file)
                if (file) {
                  const reader = new FileReader()
                  reader.onloadend = () => {
                    setLogoPreview(reader.result as string)
                  }
                  reader.readAsDataURL(file)
                } else {
                  setLogoPreview(null)
                }
              }}
              preview={logoPreview}
              label="Logo"
              showPreview={true}
              maxSizeMB={5}
            />
          </div>

          <div className={`${handles.formRow} grid grid-cols-2 gap-4`}>
            <div className={`${handles.formGroup}`}>
              <label htmlFor="sortOrder" className={`${handles.formLabel} block text-sm font-medium text-gray-700 mb-1.5`}>
                Ordem
              </label>
              <Input
                type="number"
                id="sortOrder"
                {...register('sortOrder', {
                  valueAsNumber: true,
                  min: { value: 0, message: 'Ordem deve ser maior ou igual a 0' }
                })}
                error={!!errors.sortOrder}
                helperText={errors.sortOrder?.message}
              />
            </div>

            <div className={`${handles.formGroup}`}>
              <label htmlFor="isActive" className={`${handles.formLabel} block text-sm font-medium text-gray-700 mb-1.5`}>
                Status
              </label>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <Select
                    id="isActive"
                    value={field.value}
                    onChange={field.onChange}
                    options={[
                      { value: 'true', label: 'Ativo' },
                      { value: 'false', label: 'Inativo' }
                    ]}
                    placeholder="Selecione o status"
                    className={handles.formSelect}
                  />
                )}
              />
            </div>
          </div>

          <div className={`${handles.modalFooter} flex justify-end gap-3 pt-4`}>
            <button
              type="button"
              onClick={() => {
                setIsModalOpen(false)
                setEditingBrand(null)
                setLogoFile(null)
                setLogoPreview(null)
                reset({
                  name: '',
                  description: '',
                  website: '',
                  isActive: 'true',
                  sortOrder: 0
                })
              }}
              className={`${handles.cancelButton} rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50`}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className={`${handles.saveButton} rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50`}
            >
              {isSaving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false)
          setBrandToDelete(null)
        }}
        onConfirm={confirmDeleteBrand}
        title="Confirmar Exclusão"
        message={`Tem certeza que deseja excluir a marca "${brandToDelete?.name}"? Esta ação não pode ser desfeita.`}
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
        confirmButtonClassName="bg-red-600 hover:bg-red-700"
        isLoading={pendingDeletion !== null}
      />
    </AdminLayout>
  )
}

export default AdminBrands
